import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { confirm } from "../../common/Confirm/confirm";
import { toast } from '../../common/Toast/toast';
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import AddClassroom from "./AddClassroom.jsx";
import PopupDone, { usePopupDone } from "../../common/PopupDone/PopupDone.jsx";
import "../../Trainer/TrainerDashboard.css";
import "../../common/lms-pages.css";
import {
  getClassrooms,
  deleteClassroom,
} from "../../../../services/ClassroomService.js";
import { useAuth } from "../../../../context/AuthContext";
import FilterSheet from "../../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../../common/useDebouncedCallback";

const ListClassroom = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedField, setSelectedField] = useState("classroomNo");
  const [details, setDetails] = useState(null);
  const [formModal, setFormModal] = useState(null);
  // A change saved in either popup is confirmed in it (the done panel),
  // which then closes itself. The form, opened over the view, is newer.
  const { done: detailsDone } = usePopupDone(Boolean(details));
  const { done: formDone } = usePopupDone(Boolean(formModal));
  const [loadingMore, setLoadingMore] = useState(false);

  // The boxes hold what was typed; the applied values below are what the
  // list is actually filtered by, so a keystroke does not refetch on its own.
  const [uiSearchTerm, setUiSearchTerm] = useState("");

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);

  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const isFetching = useRef(false);
  const lastFetchRef = useRef("");
  const { hasPermission } = useAuth();

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: "success",
      active: "primary",
      reserved: "warning",
      maintenance: "danger",
    };

    const key = status?.toLowerCase() || "";
    const variant = statusConfig[key] || "secondary";
    const displayStatus = status
      ? status.charAt(0).toUpperCase() + status.slice(1)
      : "N/A";

    return (
      <span>
        {displayStatus}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const fetchClassrooms = useCallback(async () => {
    const fetchKey = `${recordsPerPage}-${currentPage}-${searchTerm}-${selectedField}`;

    if (lastFetchRef.current === fetchKey) return;
    if (isFetching.current) return;

    isFetching.current = true;
    lastFetchRef.current = fetchKey;

    try {
      const isAppend = appendRef.current;
      if (isAppend) setLoadingMore(true);
      else setLoading(true);
      const response = await getClassrooms(
        recordsPerPage,
        currentPage,
        searchTerm,
        selectedField,
      );

      const classroomsData = response.data || [];
      const pagination = response.pagination || {};

      const formattedData = classroomsData.map((item, index) => ({
        ...item,
        srno: (currentPage - 1) * recordsPerPage + index + 1,
        statusBadge: getStatusBadge(item.status),
        formattedDate: formatDate(item.date),
        formattedStartTime: formatDateTime(item.startTime),
        formattedEndTime: formatDateTime(item.endTime),
      }));

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setClassrooms((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...formattedData.filter((r) => !seen.has(r.id))];
        });
      } else {
      setClassrooms(formattedData);
      }
      appendRef.current = false;      setTotalRecords(pagination.total || 0);
    } catch (err) {
      console.error("Error fetching classrooms:", err);
      toast.error("Failed to fetch classroom list");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      isFetching.current = false;
    }
  }, [recordsPerPage, currentPage, searchTerm]);

  // Fetch classrooms when dependencies change
  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  // Permission check
  useEffect(() => {
    if (!hasPermission("list-classroom", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view classrooms.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const handleDelete = async (id) => {
    const answer = await confirm({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this classroom?",
      tone: 'danger',
      confirmButtonText: "Yes, Delete it!",
    });

    if (answer.isConfirmed) {
      try {
        await deleteClassroom(id);
        // Confirmed in the popup, which then closes itself.
        toast.success("Classroom deleted successfully");

        // Reset the fetch flag to force a new fetch
        lastFetchRef.current = "";

        // Check if this was the last item on the current page
        if (classrooms.length === 1 && currentPage > 1) {
          // If we deleted the last item on this page, go to previous page
          setCurrentPage(currentPage - 1);
        } else {
          // Otherwise, refresh the current page
          await fetchClassrooms();
        }
      } catch (error) {
        console.error("Error deleting classroom:", error);
        toast.error("Failed to delete classroom");
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/edit-classroom/${id}`);
  };

  const hasMore = classrooms.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, classrooms.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSelectedField(field);
    runSearch(value);
  };

  const headers = [
    {
      title: "Classroom",
      prop: "classroomNo",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.classroomNo || "Unnamed"}</span>
          {row.branch?.name && (
            <span className="lp-sub">{row.branch.name}</span>
          )}
        </div>
      ),
    },
    {
      title: "Floor",
      prop: "floor",
      cell: (row) => <span className="lp-plain">{row.floor || "—"}</span>,
    },
    {
      title: "Capacity",
      prop: "capacity",
      sortable: true,
      cell: (row) => <span className="lp-num">{row.capacity ?? "—"}</span>,
    },
    {
      title: "When",
      prop: "formattedDate",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-when">{row.formattedDate || "—"}</span>
          {(row.formattedStartTime || row.formattedEndTime) && (
            <span className="lp-sub">
              {row.formattedStartTime || "—"} – {row.formattedEndTime || "—"}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      prop: "statusBadge",
      cell: (row) => row.statusBadge,
    },
  ];

  const searchableFields = [
    { value: "classroomNo", label: "Classroom" },
    { value: "description", label: "Description" },
    { value: "floor", label: "Floor" },
  ];

  const canEdit = hasPermission("list-classroom", "update", "lms");
  const canDelete = hasPermission("list-classroom", "delete", "lms");

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission("list-classroom", "add", "lms") && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ recordId: null })}
            >
              Add classroom
            </button>
          )}
        </div>
      </header>

      <FilterSheet label="Filter classrooms">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-room-search">
              Search
            </label>
            <input
              id="lp-room-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by classroom, description or floor..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, selectedField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-room-field">
              Search in
            </label>
            <select
              id="lp-room-field"
              className="pg-select"
              value={selectedField}
              onChange={(e) => onSearchChange(uiSearchTerm, e.target.value)}
            >
              {searchableFields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-room-rows">
              Rows per load
            </label>
            <select
              id="lp-room-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={classrooms}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more classrooms...</span>
          </>
        ) : hasMore ? (
          <span>
            {classrooms.length} of {totalRecords} classrooms
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} classroom{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {/* Adding a classroom is one step: which room, where, and when. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-room-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFormModal(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Classroom</span>
                <h3 className="pg-modal-title" id="lp-room-form-title">
                  {formModal.recordId ? "Edit classroom" : "Add a classroom"}
                </h3>
                <p className="pg-modal-sub">
                  Which room, where it is, and when it is in use.
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
              <AddClassroom
                embedded
                recordId={formModal.recordId}
                onCancel={() => setFormModal(null)}
                onDone={() => {
                  // Saved: the form's toast becomes this dialog's done panel,
                  // which closes the dialog by itself.
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  setDetails(null);
                  appendRef.current = false;
                  lastFetchRef.current = null;
                  setCurrentPage(1);
                  fetchClassrooms();
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-room-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Classroom</span>
                <h3 className="pg-modal-title" id="lp-room-title">
                  {details.classroomNo || "Unnamed"}
                </h3>
                {details.branch?.name && (
                  <p className="pg-modal-sub">{details.branch.name}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {detailsDone ? (
                <PopupDone message={detailsDone} onClose={() => setDetails(null)} />
              ) : (
              <>
              <dl className="quiz-facts">
                <div>
                  <dt>Status</dt>
                  <dd>{details.statusBadge}</dd>
                </div>
                <div>
                  <dt>Floor</dt>
                  <dd>{details.floor || "—"}</dd>
                </div>
                <div>
                  <dt>Capacity</dt>
                  <dd>{details.capacity ?? "—"}</dd>
                </div>
                <div>
                  <dt>Date</dt>
                  <dd>{details.formattedDate || "—"}</dd>
                </div>
                <div>
                  <dt>Starts</dt>
                  <dd>{details.formattedStartTime || "—"}</dd>
                </div>
                <div>
                  <dt>Ends</dt>
                  <dd>{details.formattedEndTime || "—"}</dd>
                </div>
              </dl>

              {details.description && (
                <>
                  <h5 className="pgm-section-title">Description</h5>
                  <p className="lp-prose">{details.description}</p>
                </>
              )}
              </>
              )}
            </div>

            {!detailsDone && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const id = details.id;
                      setFormModal({ recordId: id });
                    }}
                  >
                    Edit classroom
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const id = details.id;
                      handleDelete(id);
                    }}
                  >
                    Delete classroom
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListClassroom;
